/**
 * Presentation vocabulary and text safety.
 *
 * Shared by every renderer. Pure — no DOM access, so it is unit-testable and usable
 * from the Worker if server-side rendering is ever wanted.
 */

/** The six Atlas principles, in scoring order. Indexes align with `principle_scores`. */
export const PRINCIPLE_LABELS = [
  'Scale and Liquidity',
  'Return Profile and Risk',
  'Foreign-Investor Accessibility',
  'DeFi Fit',
  'Net-Return Efficiency',
  'Incumbent Coverage Gap',
] as const;

/** Distribution tier codes → their expanded names. */
export const TIER_LABELS: Readonly<Record<string, string>> = {
  A1: 'On-Chain Composability',
  A2: 'Regulated DeFi Vaults',
  B: 'Institutional Wholesale',
};

/** `"A1 + A2"` → `"On-Chain Composability (A1) + Regulated DeFi Vaults (A2)"`. */
export function expandTierLabel(tier: string | null | undefined): string {
  if (!tier) return '';
  return tier
    .split(/\s*\+\s*/)
    .map((part) => {
      const t = part.trim();
      const label = TIER_LABELS[t];
      return label ? `${label} (${t})` : t;
    })
    .join(' + ');
}

/** `"Not Eligible"` → `"not-eligible"`, for CSS class names. */
export function statusClass(status: string | null | undefined): string {
  if (!status) return 'eligible';
  return status.toLowerCase().replace(/\s+/g, '-');
}

/** Escape text for interpolation into HTML. Matches the legacy `esc()` exactly. */
export function esc(s: unknown): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Inline tags the curated copy legitimately uses inside analytical prose.
 * Deliberately tiny: emphasis only, no attributes, no links, no structure.
 */
const ALLOWED_INLINE_TAGS = new Set(['strong', 'em', 'b', 'i']);

/**
 * Escape a rich-text field while preserving a small allowlist of inline emphasis tags.
 *
 * SECURITY. The legacy build interpolated three model-influenced fields straight into
 * innerHTML with no escaping at all — `principle_rationale[].bullets[].detail`,
 * `distribution_rationale.sleeve_fit[]`, and the `venues[]` lists. For cached assets
 * that is curated copy, but for off-cache lookups those values are model output
 * derived from live web search, so a crafted page could inject markup and script into
 * the scorecard.
 *
 * Blanket-escaping would have been the safe fix but a visible regression: 109 of the
 * 196 curated `detail` fields use `<strong>` for the leading figure. So this escapes
 * everything, then restores only the allowlisted emphasis tags. Attributes are never
 * preserved, so `<strong onmouseover=...>` cannot survive.
 */
export function sanitizeRichText(input: unknown): string {
  if (input === null || input === undefined) return '';
  const escaped = esc(input);
  const tagPattern = new RegExp(`&lt;(/?)(${[...ALLOWED_INLINE_TAGS].join('|')})&gt;`, 'gi');
  return escaped.replace(tagPattern, (_m, slash: string, tag: string) => `<${slash}${tag.toLowerCase()}>`);
}

/** Pluralise by count: `plural(1,'asset')` → `'asset'`, `plural(2,'asset')` → `'assets'`. */
export function plural(n: number, word: string, suffix = 's'): string {
  return n === 1 ? word : word + suffix;
}
